import React, { useContext, useCallback } from "react";
import styled, { css } from "styled-components";
import { Alert, Modal, Button } from "components/bootstrap";
import StreamLink from "components/streams/StreamLink";
import { useStore } from "stores/connect";
import { StreamsStore } from "views/stores/StreamsStore";
import MessageFavoriteFieldsContext from "views/components/contexts/MessageFavoriteFieldsContext";
import StringUtils from "util/StringUtils";
import useMessageFavoriteFieldsForEditing, {
  DEFAULT_FIELDS
} from "views/components/messagelist/MessageFields/hooks/useMessageFavoriteFieldsForEditing";
import { ModalSubmit } from "components/common";
import MessageFieldsEditModeLists from "views/components/messagelist/MessageFields/MessageFieldsEditModeLists";
import useSendFavoriteFieldTelemetry from "views/components/messagelist/MessageFields/hooks/useSendFavoriteFieldTelemetry";
const FieldsContainer = styled.div(
  ({ theme }) => css`
    overflow: visible;
    padding: ${theme.spacings.xs} 0;
  `
);
const ModalContentContainer = styled.div`
  font-size: ${({ theme }) => theme.fonts.size.small};
`;
const StyledAlert = styled(Alert)`
  margin-top: 0;
`;
const resetActionTitle = `Reset to default fields: ${DEFAULT_FIELDS.join(", ")}`;
const MessageFieldsEditModal = ({ toggleEditMode }) => {
  const {
    editingFavoriteFields,
    saveEditedFavoriteFields,
    resetFavoriteFields,
    reorderFavoriteFields,
    onFavoriteToggle
  } = useMessageFavoriteFieldsForEditing();
  const sendFavoriteFieldTelemetry = useSendFavoriteFieldTelemetry();
  const { message, editableStreams } = useContext(MessageFavoriteFieldsContext);
  const messageStreams = useStore(
    StreamsStore,
    ({ streams }) => streams.filter((stream) => message.fields.streams.includes(stream.id))
  );
  const _saveFavoriteField = useCallback(() => {
    saveEditedFavoriteFields();
    toggleEditMode();
  }, [saveEditedFavoriteFields, toggleEditMode]);
  const _resetFavoriteFields = useCallback(() => {
    resetFavoriteFields();
    toggleEditMode();
  }, [resetFavoriteFields, toggleEditMode]);
  const _toggleEditMode = useCallback(() => {
    sendFavoriteFieldTelemetry("EDIT_CANCELED");
    return toggleEditMode();
  }, [sendFavoriteFieldTelemetry, toggleEditMode]);
  return /* @__PURE__ */ React.createElement(Modal, { onHide: _toggleEditMode, show: true }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, "Favorite fields configuration")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(ModalContentContainer, null, /* @__PURE__ */ React.createElement(FieldsContainer, null, /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "info" }, "Favorite fields will appear in all streams this message belongs to, including", " ", StringUtils.pluralize(editableStreams.length, " stream ", " streams "), editableStreams.map((stream, index) => {
    const isLast = index === messageStreams.length - 1;
    return /* @__PURE__ */ React.createElement(React.Fragment, { key: stream.id }, /* @__PURE__ */ React.createElement(StreamLink, { key: stream.id, stream }), !isLast && ", ");
  }), " ", "in which this message is routed."), /* @__PURE__ */ React.createElement(
    MessageFieldsEditModeLists,
    {
      reorderFavoriteFields,
      onFavoriteToggle,
      editingFavoriteFields
    }
  )))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText: "Save Configuration",
      leftCol: /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick: _resetFavoriteFields, title: resetActionTitle }, "Reset to default"),
      onCancel: toggleEditMode,
      onSubmit: _saveFavoriteField,
      submitButtonType: "button"
    }
  )));
};
export default MessageFieldsEditModal;
